import { Inject, Injectable } from "@angular/core";
import { MAT_DIALOG_DATA } from "@angular/material/dialog";
import { ISignatureConfiguration } from "core/dto";
import { filterAllTruthyInTuple, subjectFromObs } from "core/rx-js";
import { LoaderService, SnackbarService } from "core/services";
import { DsgDeepTranslationKey } from "projects/designer/src/app/pipes/translate.pipe";
import { BehaviorSubject, Subject, combineLatest, distinctUntilChanged, filter, map, take } from "rxjs";
import { ITask } from "../../../dto-map/i-task";
import { ISignatureOptionsData } from "./signature-options.component";


@Injectable()
export class SignatureOptionsService {
  private readonly _destroy = new Subject<void>();

  private readonly _remoteTaskService = this.data.remoteTaskService;
  private readonly _remoteActivityService = this.data.remoteActivityService;

  readonly tasksSubject = this.data.signatureCollectionService.tasksSubject;
  readonly signatureTasksObs = this.tasksSubject.pipe(
    filter(Boolean),
    map((tasks) => tasks.filter(({ type }) => type === 'SIGNATURE') as ITask<'SIGNATURE'>[]),
  )



  readonly signatureTaskSubject = this.data.signatureService.signatureTaskSubject;
  get signatureTask() {
    return this.signatureTaskSubject.value!;
  }

  readonly allowedSignatureTypesSubject = new BehaviorSubject<string[]>(this.signatureTask.configuration.allowedSignatureTypes)
  get allowedSignatureTypes() {
    return this.allowedSignatureTypesSubject.value;
  }

  readonly setUpSignatureTypesObs = this.data.signatureService.setUpSignatureTypesObs;
  readonly backgroundObs = this.data.signatureService.backgroundObs;
  readonly activityObs = this.data.signatureService.activityObs

  readonly batchNameSubject = new BehaviorSubject<string>(this.signatureTask.batchName);
  get batchName() {
    return this.batchNameSubject.value;
  }

  readonly activitySubject = subjectFromObs({
    observable: this.activityObs,
    initialValue: null,
    until: this._destroy
  })

  get activity() {
    return this.activitySubject.value;
  }

  readonly batchReadonlyObs = combineLatest([this._remoteActivityService.activityBatchSigningSubject, this.activitySubject]).pipe(
    filterAllTruthyInTuple(),
    map(([activityBatchSigning, activity]) => activityBatchSigning[activity.id]),
    distinctUntilChanged(),
    map((batchSigning) => batchSigning !== 'custom')
  )


  readonly activityBatchesObs = combineLatest([
    this.signatureTasksObs,
    this.activitySubject
  ])
    .pipe(
      map(([signatureTasks, activity]) =>
        signatureTasks
          .filter(({ activityId, batchName }) => activityId === activity?.id && batchName)
          .map(({ batchName }) => batchName)
      ),
      map((batchNames) => [...new Set(batchNames)])
    )


  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ISignatureOptionsData,
    private readonly _snackbarService: SnackbarService<DsgDeepTranslationKey>,
    private readonly _loaderService: LoaderService<DsgDeepTranslationKey>
  ) {

  }


  save() {

    const id = this._loaderService.show({ key: 'update-signature-configuration', text: 'ss-entities.task.update-signature-configuration.pending' })
    this._remoteTaskService.update(
      this.signatureTask.id,
      {
        ...this.signatureTask,
        batchName: this.batchName,
        configuration: {
          ...(this.signatureTask.configuration || {}),
          allowedSignatureTypes: this.allowedSignatureTypesSubject.value,
        } as ISignatureConfiguration
      },
      'reloadChangedRemote'
    )
      .pipe(
        this._snackbarService.handleAsync({
          errorMessage: 'ss-entities.task.update-signature-configuration.error',
          successMessage: 'ss-entities.task.update-signature-configuration.success',
        }),
        take(1),
      )
      .subscribe(() => {
        this.data.signatureOptionsDialogService.close();
      })
  }

  openActorConfig() {

    this.data.actorSplugInfoDialogService.open(this.activity.actor)
  }

  ngOnDestroy() {
    this._destroy.complete();
    this._destroy.next();
  }
}
